import type { Translations } from "@zplanem/translations"
import { Button } from "@/components/ui/button"
import { cn } from "../../../lib/utils"
import type { TasksViewMode } from "../../hooks/use-tasks-view-mode"

type TicketCopy = Translations["tickets"]

function PlusIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

/**
 * Kanban|List segmented control. Desktop-only (`hidden md:inline-flex`) — mobile
 * is List-only, so the toggle never renders there.
 */
export function TicketViewSwitcher({
	value,
	onChange,
	t,
}: {
	value: TasksViewMode
	onChange: (mode: TasksViewMode) => void
	t: TicketCopy
}) {
	// Toggle-button group (aria-pressed) rather than radiogroup/radio — each
	// button names itself; a fieldset + sr-only legend names the group.
	return (
		<fieldset className="hidden md:inline-flex items-center gap-1 rounded-xl border border-border bg-muted p-1">
			<legend className="sr-only">{t.viewSwitcherLabel}</legend>
			<SegmentButton
				active={value === "kanban"}
				label={t.viewBoard}
				onClick={() => onChange("kanban")}
			/>
			<SegmentButton
				active={value === "list"}
				label={t.viewList}
				onClick={() => onChange("list")}
			/>
		</fieldset>
	)
}

function SegmentButton({
	active,
	label,
	onClick,
}: {
	active: boolean
	label: string
	onClick: () => void
}) {
	return (
		<button
			type="button"
			aria-pressed={active}
			onClick={onClick}
			className={cn(
				"rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
				active
					? "bg-primary text-primary-foreground"
					: "text-foreground/70 hover:text-foreground",
			)}
		>
			{label}
		</button>
	)
}

/**
 * Add-task button. Labelled brand button on desktop; a minimal icon-only `+` on
 * mobile (mirrors the projects-list create button + the top-bar back button).
 */
export function TicketAddButton({
	onClick,
	t,
}: {
	onClick: () => void
	t: TicketCopy
}) {
	return (
		<Button variant="brand" onClick={onClick} aria-label={t.createCta}>
			<PlusIcon className="md:hidden" />
			<span className="hidden md:inline">{t.createCta}</span>
		</Button>
	)
}
